import { h, Component } from 'preact';
import { joinClasses } from 'utils/utils';
import { PinchGesture, RotateGesture, Plus, Minus, RotateLeft, RotateRight } from 'components/bolt/icons/quizPhotoboothIcons';
import { scale } from 'utils/math';
import Store from 'store/store';
import HTML from 'components/core/html/html';
import s from 'components/bolt/quizPhotobooth/photoEdit.sss';

export default class PhotoEdit extends Component {
	constructor(props) {
		super(props);
		this.drawHeadshot = this.drawHeadshot.bind(this);
		this.handlePointerDown = this.handlePointerDown.bind(this);
		this.handlePointerMove = this.handlePointerMove.bind(this);
		this.handlePointerUp = this.handlePointerUp.bind(this);
		this.onZoomInClick = this.onZoomInClick.bind(this);
		this.onZoomOutClick = this.onZoomOutClick.bind(this);
		this.onCounterRotateClick = this.onCounterRotateClick.bind(this);
		this.onClockwiseRotateClick = this.onClockwiseRotateClick.bind(this);
		this.onRetake = this.onRetake.bind(this);
		this.onConfirm = this.onConfirm.bind(this);

		// For easy overrides
		this.canvasSize = { width: 720, height: 720 };

		if (typeof window !== 'undefined') {
			this.setUpTouchValues();
		}
	}

	componentDidMount() {
		document.addEventListener('pointermove', this.handlePointerMove);
		document.addEventListener('pointerup', this.handlePointerUp);
		document.addEventListener('pointercancel', this.handlePointerUp);
		if (this.props.showEditScreen) {
			this.initCanvas();
		}
	}

	componentWillUnmount() {
		this.unmounted = true;
		cancelAnimationFrame(this.timer);
		document.removeEventListener('pointermove', this.handlePointerMove);
		document.removeEventListener('pointerup', this.handlePointerUp);
		document.removeEventListener('pointercancel', this.handlePointerUp);
	}

	componentDidUpdate(prevProps) {
		if (this.props.showEditScreen && !prevProps.showEditScreen) {
			this.initCanvas();
		}
	}

	setUpTouchValues() {
		this.transform = new DOMMatrix();
		this.touches = new Map();
	}

	processTouch({ pointerId = 'default', clientX, clientY }) {
		const rect = this.$canvasContainer.getBoundingClientRect();
		return {
			identifier: pointerId,
			x: scale(clientX, rect.left, rect.right, 0, this.$canvas.width),
			y: scale(clientY, rect.top, rect.bottom, 0, this.$canvas.height)
		};
	}
	updateTouch(touch) {
		this.touches.set(touch.identifier, touch);
	}
	clearTouch(touch) {
		return this.touches.delete(touch.identifier);
	}

	handlePointerDown(e) {
		if (e.pointerType === 'mouse' && e.button !== 0) {
			return;
		}
		e.preventDefault();
		this.updateTouch(this.processTouch(e));
	}

	handlePointerMove(e) {
		const touch = this.processTouch(e);
		if (!this.touches.has(touch.identifier)) {
			return;
		}
		e.preventDefault();
		this.updateTransform(touch, ...[...this.touches.values()].filter(t => t.identifier !== touch.identifier));
		this.updateTouch(touch);
	}

	handlePointerUp(e) {
		const hadTouch = this.clearTouch(this.processTouch(e));
		if (hadTouch) {
			Store.emit('TRACK_QUIZPHOTOBOOTH_PHOTO_REPOSITIONED');
		}
	}

	updateTransform(touch1, touch2) {
		const touch1From = this.touches.get(touch1?.identifier);
		if (!touch1From) return;

		if (!touch2) {
			// Single touch, simple translation
			this.transform.preMultiplySelf(new DOMMatrix().translateSelf(touch1.x - touch1From.x, touch1.y - touch1From.y));
			return;
		}

		const touch2From = this.touches.get(touch2.identifier);
		if (!touch2From) return;

		// Double-touch
		// Multiple ways to do this but here we're basically following this answer: https://math.stackexchange.com/a/2790865

		const { x: sCos, y: sSin, z: tx, w: ty } = new DOMMatrix([
			touch1From.x, touch1From.y, touch2From.x, touch2From.y,
			-touch1From.y, touch1From.x, -touch2From.y, touch2From.x,
			1, 0, 1, 0,
			0, 1, 0, 1,
		])
			.invertSelf()
			.transformPoint(new DOMPoint(touch1.x, touch1.y, touch2.x, touch2.y));

		// If the matrix couldn't be inverted, all the values are NaN (just do nothing in this case)
		if (isNaN(sCos)) {
			return;
		}

		this.transform.preMultiplySelf(new DOMMatrix([sCos, sSin, -sSin, sCos, tx, ty]));
	}

	onRetake() {
		const { retakePhoto } = this.props;
		this.setUpTouchValues();
		Store.emit('TRACK_QUIZPHOTOBOOTH_RETAKE_OR_CONTINUE', 'retake-photo');
		retakePhoto();
	}

	onConfirm() {
		const { confirm } = this.props;
		cancelAnimationFrame(this.timer);

		// Copy of photo for later use (avoid preact destroying it)
		const photo = document.createElement('canvas');
		photo.width = this.$canvas.width;
		photo.height = this.$canvas.height;
		photo.getContext('2d').drawImage(this.$canvas, 0, 0);
		Store.get().quizPhotobooth.set({ photo });

		Store.emit('TRACK_QUIZPHOTOBOOTH_RETAKE_OR_CONTINUE', 'continue');
		confirm();
		// Store.emit('TRACK_QUIZPHOTOBOOTH_QUIZ_VIEW', 'shareable');
	}

	centeredZoom(factor) {
		this.transform.preMultiplySelf(
			new DOMMatrix().scaleSelf(
				factor, factor, undefined,
				this.$canvas.width / 2, this.$canvas.height / 2, undefined
			)
		);
	}

	onZoomInClick() {
		this.centeredZoom(1.1);
		Store.emit('TRACK_QUIZPHOTOBOOTH_ZOOM_CLICK', 'plus');
	}

	onZoomOutClick() {
		this.centeredZoom(1 / 1.1);
		Store.emit('TRACK_QUIZPHOTOBOOTH_ZOOM_CLICK', 'minus');
	}

	centeredRotate(angleDegrees) {
		const x = this.$canvas.width / 2;
		const y = this.$canvas.height / 2;
		this.transform.preMultiplySelf(
			new DOMMatrix()
				.translateSelf(x, y)
				.rotateSelf(angleDegrees)
				.translateSelf(-x, -y)
		);
	}

	onCounterRotateClick() {
		this.centeredRotate(-3);
		Store.emit('TRACK_QUIZPHOTOBOOTH_ROTATE_CLICK', 'counterClockwise');
	}

	onClockwiseRotateClick() {
		this.centeredRotate(3);
		Store.emit('TRACK_QUIZPHOTOBOOTH_ROTATE_CLICK', 'clockwise');
	}

	initCanvas() {
		const { headshotData } = this.props;
		this.canvasContext = this.$canvas.getContext('2d');
		if (headshotData) {
			this.headshotImg = new Image();
			this.headshotImg.src = headshotData;
			this.headshotImg.onload = this.drawHeadshot;
		}
	}

	getDrawCoords() {
		const destCoords = { width: this.$canvas.width, height: this.$canvas.height };
		const destRatioLower = destCoords.width / destCoords.height < this.headshotImg.width / this.headshotImg.height;
		// COVER or CONTAIN - TODO: option somewhere instead of hardcoded ?
		const drawMode = 'COVER';

		if (destRatioLower === (drawMode === 'COVER')) {
			destCoords.width = this.headshotImg.width * destCoords.height / this.headshotImg.height;
		} else {
			destCoords.height = this.headshotImg.height * destCoords.width / this.headshotImg.width;
		}

		return {
			from: [0, 0, this.headshotImg.width, this.headshotImg.height],
			to: [
				(this.$canvas.width - destCoords.width) / 2,
				(this.$canvas.height - destCoords.height) / 2,
				destCoords.width,
				destCoords.height
			]
		};
	}

	drawHeadshot() {
		if (this.unmounted) {
			return;
		}
		if (!this.props.showEditScreen) return;
		this.canvasContext.clearRect(0, 0, this.$canvas.width, this.$canvas.height);
		this.canvasContext.save();
		this.canvasContext.setTransform(this.transform);
		const coords = this.getDrawCoords();
		this.canvasContext.drawImage(
			this.headshotImg,
			...coords.from,
			...coords.to
		);
		this.canvasContext.restore();
		cancelAnimationFrame(this.timer);
		this.timer = requestAnimationFrame(this.drawHeadshot);
	}

	renderMobileInstructions(fontStyles) {
		const copy = this.props?.copy;
		return (
			<div class={s.touchInstructions}>
				<div class={joinClasses(s.zoomInstructions, fontStyles.quizPhotoboothMessage)}>
					<PinchGesture />
					<span>{copy.$QUIZPHOTOBOOTH_PINCH_TO_ZOOM}</span>
				</div>
				<div class={joinClasses(s.rotateInstructions, fontStyles.quizPhotoboothMessage)}>
					<RotateGesture />
					<span>{copy.$QUIZPHOTOBOOTH_ROTATE_FINGERS}</span>
				</div>
			</div>
		);
	}

	renderDesktopOverlay() {
		return (
			<div class={s.desktopOverlay} />
		);
	}

	renderPreviewCanvas() {
		return (
			<canvas ref={($ref) => this.$canvas = $ref} class={s.photoCanvas} width={this.canvasSize.width} height={this.canvasSize.height} />
		);
	}

	renderPositionButtons() {
		return (
			<div class={s.positionBtns}>
				<button class={s.shortBtn} onClick={this.onCounterRotateClick}>
					<RotateLeft />
				</button>
				<button class={s.shortBtn} onClick={this.onClockwiseRotateClick}>
					<RotateRight />
				</button>
				<button class={s.shortBtn} onClick={this.onZoomOutClick}>
					<Minus />
				</button>
				<button class={s.shortBtn} onClick={this.onZoomInClick}>
					<Plus />
				</button>
			</div>
		);
	}

	render({ showEditScreen, isDesktopDevice }) {
		const copy = this.props?.copy;
		const fontStyles = this.props.appData?.styling?.fontVariables || {};
		return (
			<div class={joinClasses(s.container, showEditScreen && s.show)}>
				<HTML class={joinClasses(s.picTip, s.picPosition, fontStyles.quizPhotoboothQuestion)} content={isDesktopDevice ? copy.$QUIZPHOTOBOOTH_CLICK_AND_DRAG_DESKTOP : copy.$QUIZPHOTOBOOTH_CLICK_AND_DRAG_MOBILE} markdown />
				<div
					class={s.photoContainer}
					ref={ref => this.$canvasContainer = ref}
					onPointerDown={this.handlePointerDown}
				>
					{this.renderPreviewCanvas()}
				</div>
				{!isDesktopDevice && this.renderMobileInstructions(fontStyles)}
				{this.renderPositionButtons()}
				<div class={s.actionBtns}>
					<button class={joinClasses(s.longBtn, s.clear, fontStyles.quizPhotoboothButton)} onClick={this.onRetake}>{copy.$QUIZPHOTOBOOTH_RETAKE}</button>
					<button class={joinClasses(s.longBtn, fontStyles.quizPhotoboothButton)} onClick={this.onConfirm}>{copy.$QUIZPHOTOBOOTH_CONTINUE}</button>
				</div>
			</div>
		);
	}
}
